import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent,
  type ReactNode,
} from 'react';

import clsx from 'clsx';
import { Link, matchPath, useHistory, useLocation } from 'react-router-dom';
import { useForceDocumentNavigation } from '@hungryroot/environment';
import { useExperimentFlags } from '@hungryroot/experiments';
import { createHomeFeedRefreshLocationState } from '@hungryroot/feed';
import { useNextOrderV3 } from '@hungryroot/rest-hooks';
import {
  BROWSE_GROCERIES_ROUTE,
  BROWSE_ROUTE,
  EDIT_DELIVERY_RECIPES_ROUTE,
  MY_COOKBOOK_ROUTE,
  MY_HUNGRYROOT_ROUTE,
  ORDERS_PLACEHOLDER_ROUTE,
} from '@hungryroot/routes';
import { buttonClickedEvent, componentInteraction } from '@hungryroot/segment';
import {
  Paragraph,
  Box4,
  Heart3,
  Home2,
  MagnifyingGlass2,
} from '@hungryroot/ui-kit';
import { useCustomerCookbooksEnabled } from '../../../exp-252-cookbooks/hooks/useCustomerCookbooksEnabled';
import { useDocumentAwareNavigate } from '../../../hooks/useDocumentAwareNavigate';
import { useIsRootyEnabled } from '../../../smartcart/SmartCartAgent/hooks/useIsRootyEnabled';
import { SmartCartAgentPanel } from '../../../smartcart/SmartCartAgent/SmartCartAgentPanel';
import { getStoredConversationId } from '../../../smartcart/SmartCartAgent/utils/conversationStorage';
import { RootyIcon } from '../../RootyIcon/RootyIcon';
import { useRootyIntroVariant } from '../../RootyIcon/useRootyIntroVariant';
import { useTabSessionTracking } from './useTabSessionTracking';
import styles from './MobileNavFloating.module.css';

type Props = {
  testid?: string;
  onOnboardingComplete?: () => void;
};

// Below this scroll position the feed is treated as "at the top", so a Home
// tap refreshes instead of scrolling. A few px of tolerance absorbs momentum
// and rubber-band scroll settling near zero.
const HOME_TAB_AT_TOP_THRESHOLD_PX = 4;

type TabKey = 'home' | 'search' | 'favorites' | 'orders';

type TabDefinition = {
  key: TabKey;
  label: string;
  href: string;
  icon: ReactNode;
};

const TABS: readonly TabDefinition[] = [
  {
    key: 'home',
    label: 'Home',
    href: MY_HUNGRYROOT_ROUTE,
    icon: <Home2 />,
  },
  {
    key: 'search',
    label: 'Search',
    href: EDIT_DELIVERY_RECIPES_ROUTE,
    icon: <MagnifyingGlass2 />,
  },
  {
    key: 'favorites',
    label: 'Cookbook',
    href: MY_COOKBOOK_ROUTE,
    icon: <Heart3 />,
  },
  {
    key: 'orders',
    label: 'Orders',
    href: ORDERS_PLACEHOLDER_ROUTE,
    icon: <Box4 />,
  },
];

export function MobileNavFloating(props: Props) {
  const { testid, onOnboardingComplete } = props;
  const location = useLocation();
  const history = useHistory();
  const { order } = useNextOrderV3();

  // Emit tab_session started/ended events for the treatment bottom nav. Mounted
  // here because this nav persists across SPA tab navigations, so one in-memory
  // session can span them.
  useTabSessionTracking();

  // Exp 242 (home + search redesign): in either treatment variant the Search tab
  // points to `/browse/`; in control it keeps its current
  // `/edit-delivery/recipes/` target.
  //
  const { popsicleExp242 } = useExperimentFlags([242]);
  const isSearchRedesign = popsicleExp242?.isVariant === true;
  // Customer-created cookbooks: the tab holds many cookbooks rather than one, so
  // its label pluralises. Still sits behind 242, which owns this nav —
  // MobileLayout only renders it for 242 treatment.
  const {
    isEnabled: isCustomerCookbooks,
    isLoading: isCustomerCookbooksLoading,
  } = useCustomerCookbooksEnabled();

  // Surface a Rooty entry point in the floating nav for Exp 242 customers for
  // whom Rooty is enabled (Exp 224 variant, not opted out). The 242 check is
  // defense-in-depth — MobileLayout only renders this nav for 242 treatment —
  // so the button never leaks if that changes. Rooty is also suppressed on MPA
  // (Astro / Springroll) pages, which render this nav in document mode: those
  // pages are outside the SPA and can't host the SmartCart agent panel.
  const forceDocumentNavigation = useForceDocumentNavigation();
  const { isEnabled: isRootyEnabled } = useIsRootyEnabled();
  const showRooty =
    isSearchRedesign && isRootyEnabled && !forceDocumentNavigation;

  const [isRootyOpen, setIsRootyOpen] = useState(false);
  // Mount the agent panel lazily — only once it has been opened — so its
  // chat/init hooks don't run on every page for everyone in the experiment.
  const [hasOpenedRooty, setHasOpenedRooty] = useState(false);

  const tabs = useMemo(
    () =>
      TABS.map((tab) => {
        if (isSearchRedesign && tab.key === 'search') {
          return { ...tab, href: BROWSE_ROUTE };
        }

        if (isCustomerCookbooks && tab.key === 'favorites') {
          return { ...tab, label: 'Cookbooks' };
        }

        return tab;
      }),
    [isSearchRedesign, isCustomerCookbooks]
  );

  const active = getActiveTab(location.pathname, isSearchRedesign);
  const activeIndex = active ? tabs.findIndex((tab) => tab.key === active) : -1;

  const navRef = useRef<HTMLElement>(null);
  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setHasMounted(true));
    return () => cancelAnimationFrame(id);
  }, []);

  // The profile dropdown opens as a full-screen overlay; the floating nav must
  // not sit on top of it. The dropdown is driven by the `profile` query param.
  const isProfileDropdownOpen = new URLSearchParams(location.search).has(
    'profile'
  );

  // Publish the rendered nav footprint so bottom-anchored UI (cart toast, feed
  // padding) clears the actual floating nav instead of a hard-coded estimate.
  useLayoutEffect(() => {
    if (isProfileDropdownOpen) return;

    const nav = navRef.current;
    if (nav === null) return;

    const { body } = document;
    body.dataset.floatingNav = '1';

    const publishClearance = () => {
      const { top } = nav.getBoundingClientRect();
      const clearance = Math.max(0, window.innerHeight - top);
      body.style.setProperty('--floating-nav-clearance', `${clearance}px`);
    };

    publishClearance();
    const resizeObserver = new ResizeObserver(publishClearance);
    resizeObserver.observe(nav);
    window.addEventListener('resize', publishClearance);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', publishClearance);
      delete body.dataset.floatingNav;
      body.style.removeProperty('--floating-nav-clearance');
    };
  }, [isProfileDropdownOpen]);

  // Snapshot prev → next and the duration computed against them in a single
  // commit. Without this, prev/next/duration would update across separate
  // effects and a re-render mid-flight could restart the animation with a
  // shorter duration.
  const [travel, setTravel] = useState(() => ({
    fromIndex: activeIndex,
    toIndex: activeIndex,
    durationMs: 0,
  }));
  useEffect(() => {
    setTravel((prev) => {
      if (prev.toIndex === activeIndex) {
        return prev;
      }
      const distance = Math.max(1, Math.abs(activeIndex - prev.toIndex));
      return {
        fromIndex: prev.toIndex,
        toIndex: activeIndex,
        // Longer jumps run longer so per-tab velocity stays roughly constant.
        durationMs: 200 + distance * 60,
      };
    });
  }, [activeIndex]);

  // Drive the travel keyframe via a transient data-traveling flag. The flag
  // is set whenever the snapshot above changes (after the initial mount)
  // and cleared after the snapshot's duration elapses.
  const [isTraveling, setIsTraveling] = useState(false);
  const initialMountRef = useRef(true);
  useEffect(() => {
    if (initialMountRef.current) {
      initialMountRef.current = false;
      return;
    }
    if (travel.toIndex < 0 || travel.durationMs === 0) {
      setIsTraveling(false);
      return;
    }
    setIsTraveling(true);
    const id = window.setTimeout(
      () => setIsTraveling(false),
      travel.durationMs
    );
    return () => window.clearTimeout(id);
  }, [travel]);

  const indicatorStyle = {
    '--active-index': activeIndex >= 0 ? activeIndex : 0,
    '--indicator-duration': `${travel.durationMs || 320}ms`,
  } as CSSProperties;

  if (isProfileDropdownOpen) {
    return null;
  }

  return (
    <nav
      ref={navRef}
      className={styles.nav}
      aria-label="Bottom navigation"
      data-testid={testid}
    >
      <div className={styles.pill}>
        <span
          className={styles.indicator}
          data-animated={hasMounted || null}
          data-visible={active !== undefined || null}
          data-traveling={isTraveling || null}
          style={indicatorStyle}
          aria-hidden
        />
        {tabs.map((tab) => (
          <NavTab
            key={tab.key}
            tab={tab}
            isActive={active === tab.key}
            testid={testid}
            onClick={(event) => {
              buttonClickedEvent({
                target_url_path: tab.href,
                order_id: order?.id ?? null,
                button_name: tab.key,
                page_position: 'mobile_nav',
              });
              onOnboardingComplete?.();
              // Exp 242: tapping Home while already on the home feed either
              // scrolls back to the top (if scrolled down) or, when already at
              // the top, refreshes the feed with new content. A same-route Link
              // click is otherwise a no-op.
              if (isSearchRedesign && tab.key === 'home' && active === 'home') {
                event.preventDefault();
                if (window.scrollY <= HOME_TAB_AT_TOP_THRESHOLD_PX) {
                  history.replace({
                    pathname: MY_HUNGRYROOT_ROUTE,
                    state: createHomeFeedRefreshLocationState(Date.now()),
                  });
                } else {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }
              }
            }}
          />
        ))}
      </div>
      {showRooty && (
        <RootyNavButton
          // ManageCookbookHost swaps its element type when this flag resolves,
          // remounting the whole authenticated SPA — this nav with it. Holding
          // the intro until then lets it play once, uninterrupted.
          isReadyToPlayIntro={!isCustomerCookbooksLoading}
          testid={testid}
          onClick={() => {
            buttonClickedEvent({
              order_id: order?.id ?? null,
              button_name: 'rooty',
              page_position: 'mobile_nav',
            });
            componentInteraction({
              actionType: 'viewed',
              componentType: 'panel',
              componentName: 'sc_agent',
              orderId: order?.id,
              conversationId: getStoredConversationId() ?? undefined,
            });
            setIsRootyOpen(true);
            setHasOpenedRooty(true);
            onOnboardingComplete?.();
          }}
        />
      )}
      {showRooty && hasOpenedRooty && (
        <SmartCartAgentPanel
          isOpen={isRootyOpen}
          onClose={() => setIsRootyOpen(false)}
          testid={testid ? `${testid}-rooty-panel` : undefined}
        />
      )}
    </nav>
  );
}

function getActiveTab(
  pathname: string,
  isSearchRedesign: boolean
): TabKey | undefined {
  if (
    matchPath(pathname, {
      path: MY_HUNGRYROOT_ROUTE,
      exact: true,
      strict: false,
    })
  ) {
    return 'home';
  }
  if (
    matchPath(pathname, {
      path: MY_COOKBOOK_ROUTE,
      exact: false,
      strict: false,
    })
  ) {
    return 'favorites';
  }
  if (
    matchPath(pathname, {
      path: ORDERS_PLACEHOLDER_ROUTE,
      exact: false,
      strict: false,
    })
  ) {
    return 'orders';
  }
  if (isSearchActiveTab(pathname, isSearchRedesign)) {
    return 'search';
  }
  return undefined;
}

// Exp 243 v1 highlights the Search tab across `/browse/` (incl.
// `/browse/cookbooks/`); control keeps the legacy
// `/edit-delivery/recipes/` + `/browse/groceries/` surfaces.
function isSearchActiveTab(
  pathname: string,
  isSearchRedesign: boolean
): boolean {
  if (isSearchRedesign) {
    return (
      matchPath(pathname, {
        path: BROWSE_ROUTE,
        exact: false,
        strict: false,
      }) !== null
    );
  }
  return (
    matchPath(pathname, {
      path: EDIT_DELIVERY_RECIPES_ROUTE,
      exact: false,
      strict: false,
    }) !== null ||
    matchPath(pathname, {
      path: BROWSE_GROCERIES_ROUTE,
      exact: false,
      strict: false,
    }) !== null
  );
}

type NavTabProps = {
  tab: TabDefinition;
  isActive: boolean;
  testid?: string;
  onClick: (event: MouseEvent<HTMLAnchorElement>) => void;
};

function NavTab(props: NavTabProps) {
  const { tab, isActive, testid, onClick } = props;
  const { forceDocumentNavigation } = useDocumentAwareNavigate();
  const className = clsx(styles.tab, { [styles['tab-active']]: isActive });
  const tabTestId = testid ? `${testid}-link-${tab.key}` : undefined;
  const ariaCurrent = isActive ? 'page' : undefined;

  const content = (
    <>
      <span className={styles['tab-icon']} aria-hidden>
        {tab.icon}
      </span>
      <Paragraph variant="caption">{tab.label}</Paragraph>
    </>
  );

  if (forceDocumentNavigation) {
    return (
      <a
        href={tab.href}
        className={className}
        data-testid={tabTestId}
        data-active={isActive || null}
        aria-current={ariaCurrent}
        onClick={onClick}
      >
        {content}
      </a>
    );
  }

  return (
    <Link
      to={tab.href}
      className={className}
      data-testid={tabTestId}
      data-active={isActive || null}
      aria-current={ariaCurrent}
      onClick={onClick}
    >
      {content}
    </Link>
  );
}

type RootyNavButtonProps = {
  isReadyToPlayIntro: boolean;
  testid?: string;
  onClick: () => void;
};

function RootyNavButton(props: RootyNavButtonProps) {
  const { isReadyToPlayIntro, testid, onClick } = props;
  const rootyTestId = testid ? `${testid}-rooty` : undefined;
  const { variant, resumeFromFrame, onIntroProgress, onIntroComplete } =
    useRootyIntroVariant(isReadyToPlayIntro);

  return (
    <button
      type="button"
      className={styles.rooty}
      data-testid={rootyTestId}
      aria-label="Open Rooty"
      onClick={onClick}
    >
      <RootyIcon
        variant={variant}
        resumeFromFrame={resumeFromFrame}
        onIntroProgress={onIntroProgress}
        onIntroComplete={onIntroComplete}
        className={styles['rooty-icon']}
      />
    </button>
  );
}
